/**
 * Store service
 */

import { applyMiddleware, compose, createStore } from "redux";
import type { Middleware } from "redux";
import { createLogger } from "redux-logger";
import { createMigrate, persistReducer } from "redux-persist";
import type { MigrationManifest } from "redux-persist";
import type { PersistPartial } from "redux-persist/es/persistReducer";
import {
	createStateSyncMiddleware,
	initMessageListener,
} from "redux-state-sync";
import thunkMiddleware from "redux-thunk";
import { SET_REQUESTS_IN_PROGRESS } from "../actions/action-types";

import rootReducer from "../reducers";
import accountService from "./account";
import storageService from "./storage";
import type {
	OfflineCacheEncryptionKey,
	ScryptParameters,
} from "../../types/crypto";
import type {
	AppAction,
	AppDispatch,
	AppState,
	AppStore,
	PersistentState,
} from "../../types/state";

const appReducer = rootReducer;
let store: AppStore;

type PersistedAppState = AppState & PersistPartial;
type DebugWindow = Window & {
	store?: AppStore;
	__REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: (options: object) => typeof compose;
};

export function addOfflineCacheHashingParameters(
	encryptionKey: OfflineCacheEncryptionKey,
	hashingAlgorithm = "scrypt",
	hashingParameters?: ScryptParameters,
): OfflineCacheEncryptionKey {
	if (encryptionKey.hashingAlgorithm && encryptionKey.hashingParameters) {
		return encryptionKey;
	}
	return {
		...encryptionKey,
		hashingAlgorithm,
		hashingParameters: {
			u: Math.max(14, hashingParameters?.u ?? 14),
			r: Math.max(8, hashingParameters?.r ?? 8),
			p: Math.max(1, hashingParameters?.p ?? 1),
			l: Math.max(64, hashingParameters?.l ?? 64),
		},
	};
}

export function migrateOfflineCacheEncryptionInfo<
	T extends Pick<AppState, "client" | "user">,
>(state: T): T {
	const encryptionKey = state.client.offlineCacheEncryptionKey;
	if (
		!encryptionKey ||
		(encryptionKey.hashingAlgorithm && encryptionKey.hashingParameters)
	) {
		return state;
	}
	return {
		...state,
		client: {
			...state.client,
			offlineCacheEncryptionKey: addOfflineCacheHashingParameters(
				encryptionKey,
				state.user.hashingAlgorithm,
				state.user.hashingParameters,
			),
		},
	};
}

export const initStore = async () => {
	const persistAccountId = await accountService.getCurrentId();

	const config = {
		channel: `redux_state_sync:${persistAccountId}`,
		blacklist: ["persist/PERSIST", "persist/REHYDRATE"],
	};

	const middlewares: Middleware<object, AppState>[] = [
		thunkMiddleware,
		createStateSyncMiddleware(config),
	];

	let composeEnhancers = compose; // Default to Redux's compose

	if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
		const loggerMiddleware = createLogger({
			predicate: (_getState: () => AppState, action: AppAction) =>
				action.type !== SET_REQUESTS_IN_PROGRESS,
		});
		middlewares.push(loggerMiddleware);

		// Enable Redux DevTools Extension in development
		if (
			typeof window !== "undefined" &&
			(window as DebugWindow).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
		) {
			composeEnhancers = (window as DebugWindow)
				.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__!({});
		}
	}

	const migrations: Record<
		number,
		(state: PersistedAppState) => PersistedAppState
	> = {
		0: (state) => {
			return {
				...state,
				server: {
					...state.server,
					complianceDisableUnmanagedGroups: false,
				},
				user: {
					...state.user,
					userDatastoreOverview: {
						datastores: [],
					},
				},
			};
		},
		1: (state) => {
			return {
				...state,
				client: {
					...state.client,
					lastPopupSearch: "",
				},
			};
		},
		2: (state) => {
			return {
				...state,
				server: {
					...state.server,
					disableCallbacks: true,
					allowedFileRepositoryTypes: [
						"azure_blob",
						"gcp_cloud_storage",
						"aws_s3",
						"do_spaces",
						"backblaze",
					],
				},
			};
		},
		3: (state) => {
			return {
				...state,
				user: {
					...state.user,
					serverSecretExists: ["SAML", "OIDC", "LDAP"].includes(
						state.user.authentication,
					),
				},
				server: {
					...state.server,
					complianceServerSecrets: "auto",
				},
				persistent: {
					...state.persistent,
					remoteConfigWebClientUrl: null,
				},
			};
		},
		4: (state) => {
			return {
				...state,
				server: {
					...state.server,
					complianceEnforceBreachDetection: false,
				},
			};
		},
		5: (state) => {
			return {
				...state,
				server: {
					...state.server,
					faviconServiceUrl: "",
				},
			};
		},
		6: (state) => {
			return {
				...state,
				user: {
					...state.user,
					hashingAlgorithm: "scrypt",
					hashingParameters: {
						u: 14,
						r: 8,
						p: 1,
						l: 64,
					},
				},
			};
		},
		7: (state) => {
			return {
				...state,
				server: {
					...state.server,
					complianceDisableTotp: false,
				},
			};
		},
		8: (state) => {
			return {
				...state,
				server: {
					...state.server,
					domainSynonyms: [],
					customDomainSynonyms: [],
					domainSynonymMap: {},
				},
			};
		},
		9: (state) => {
			return {
				...state,
				server: {
					...state.server,
					complianceDisableMultiplePasswordDatastores: false,
				},
			};
		},
		10: (state) => {
			return {
				...state,
				settingsDatastore: {
					...state.settingsDatastore,
					useMarkdownForNotes:
						state.settingsDatastore?.useMarkdownForNotes ?? true,
				},
			};
		},
		11: migrateOfflineCacheEncryptionInfo,
	};

	const persistConfig = {
		key: persistAccountId,
		blacklist: ["transient", "notification"],
		version: 11,
		storage: storageService.get("state"),
		debug: false,
		// redux-persist exposes only _persist; these migrations operate on our roots.
		migrate: createMigrate(migrations as unknown as MigrationManifest, {
			debug: false,
		}),
	};

	const persistedReducer = persistReducer(persistConfig, appReducer);

	store = createStore(
		persistedReducer,
		composeEnhancers(applyMiddleware<AppDispatch, AppState>(...middlewares)),
	);

	initMessageListener(store);

	// Expose store to window only in development for debugging (optional)
	if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
		(window as DebugWindow).store = store;
	}

	return store;
};

/**
 * Initializes the standalone link-share page with a private, anonymous store.
 * Only host pins and client configuration are copied from the active account.
 * There is deliberately no persistence or cross-tab state synchronization: a
 * share's backend selection and host approvals must stay within this page.
 */
export const initLinkShareStore = async () => {
	const initialState = appReducer(undefined, { type: "@@INIT_LINK_SHARE" });
	const accountId = await accountService.getCurrentId();
	const persistedState: Record<string, string> | null = JSON.parse(
		(await storageService.findKey("state", `persist:${accountId}`)) ?? "null",
	);

	if (persistedState?.persistent) {
		const persistent: PersistentState = JSON.parse(persistedState.persistent);
		const copyPersistent = <K extends keyof PersistentState>(key: K) => {
			initialState.persistent[key] = persistent[key];
		};
		for (const key of [
			"knownHosts",
			"remoteConfigWebClientUrl",
			"remoteConfigJson",
		] as const) {
			if (Object.hasOwn(persistent, key)) {
				copyPersistent(key);
			}
		}
	}

	store = createStore(
		appReducer,
		initialState,
		applyMiddleware(thunkMiddleware),
	);
	return store;
};

export const isStoreInitialized = () => {
	return store !== undefined;
};

/** Call after initStore/initLinkShareStore; isStoreInitialized guards startup. */
export const getStore = (): AppStore => {
	return store;
};
